import React, { useEffect, useState, useCallback } from "react";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Bank, Clock, Warning, Plus } from "@phosphor-icons/react";

const ACCOUNT_PALETTE = [
  { grad: "from-cyan-400 to-sky-700",  icon: "bg-cyan-900/40 text-cyan-200" },
  { grad: "from-sky-400 to-cyan-700",  icon: "bg-sky-900/40 text-sky-200" },
  { grad: "from-cyan-300 to-cyan-700", icon: "bg-cyan-900/40 text-cyan-200" },
  { grad: "from-sky-300 to-sky-700",   icon: "bg-sky-900/40 text-sky-200" },
  { grad: "from-cyan-500 to-sky-800",  icon: "bg-cyan-900/40 text-cyan-200" },
  { grad: "from-teal-400 to-cyan-700", icon: "bg-teal-900/40 text-teal-200" },
];

function freshness(lastUpload) {
  if (!lastUpload) return { label: "Never", absolute: "—", tone: "text-slate-400", stale: true };
  const d = new Date(lastUpload);
  if (Number.isNaN(d.getTime())) return { label: "—", absolute: "—", tone: "text-slate-400", stale: true };
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  const absolute = `${date}, ${time}`;
  // Compare calendar days (local) rather than elapsed hours so an upload from
  // yesterday afternoon never shows up as "Today" the next morning.
  const toLocalDateNum = (dt) =>
    dt.getFullYear() * 10000 + (dt.getMonth() + 1) * 100 + dt.getDate();
  const uploadDay = toLocalDateNum(d);
  const today     = toLocalDateNum(new Date());
  const dayDiff   = today - uploadDay;       // raw int diff (good for ±N day buckets when same yr/month)
  // For accurate multi-day diff use ms-based floor.
  const msDiff    = Date.now() - d.getTime();
  const daysAgo   = Math.floor(msDiff / 86400000);
  if (dayDiff === 0) return { label: `Today, ${time}`,     absolute, tone: "text-cyan-300",   stale: false };
  if (dayDiff === 1) return { label: `Yesterday, ${time}`, absolute, tone: "text-cyan-300",   stale: false };
  if (daysAgo <= 3)  return { label: `${daysAgo}d ago`,    absolute, tone: "text-slate-400",  stale: false };
  if (daysAgo <= 7)  return { label: `${daysAgo}d ago`,    absolute, tone: "text-amber-300",  stale: true  };
  return                    { label: `${daysAgo}d ago`,    absolute, tone: "text-cyan-200",   stale: true  };
}

// Compare the calendar day of the statement's balance ("as of") against the
// calendar day the file was uploaded. If they match, the bank balance is
// "current" — show in green. Otherwise the statement is stale relative to
// when it was uploaded — show in red and blink to demand attention.
function balanceFreshness(asOf, lastUpload) {
  if (!asOf || !lastUpload) return { current: false, isoAsOf: asOf || "" };
  const toLocalDateStr = (v) => {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const asOfStr = String(asOf).length >= 10 ? String(asOf).slice(0, 10) : toLocalDateStr(asOf);
  const uploadStr = toLocalDateStr(lastUpload);
  return { current: asOfStr === uploadStr, isoAsOf: asOfStr };
}

export default function BankAccountsCards({ refreshKey, extraCard }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/accounts");
      setAccounts(res.data || []);
    } catch { setAccounts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const total = accounts.reduce((s, a) => s + Number(a.balance || 0), 0);

  return (
    <div data-testid="bank-accounts-cards" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-sm grid place-items-center bg-indigo-100 text-indigo-700"><Bank size={15} weight="duotone" /></div>
          <h3 className="font-heading font-bold text-base tracking-tight">Bank accounts</h3>
          {accounts.length > 0 && (
            <span className="text-[11px] font-mono tabular-nums text-slate-500">
              · {accounts.length} acct · total {fmtINR(total)}
            </span>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-xs text-slate-500 py-6 text-center bg-white rounded-md border border-slate-200">Loading accounts…</div>
      )}

      {!loading && accounts.length === 0 && (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50/50 p-6 text-center" data-testid="no-accounts">
          <Bank size={28} weight="duotone" className="text-slate-400 inline-block mb-2" />
          <div className="text-sm text-slate-700 font-semibold">No bank accounts yet</div>
          <div className="text-xs text-slate-500 mt-1">
            Upload a bank statement under <span className="font-bold">Uploads → Bank</span> and set an account name like
            <span className="font-mono mx-1 px-1 bg-white border border-slate-200 rounded-sm">HDFC Current</span>
            to create your first account card here.
          </div>
        </div>
      )}

      {!loading && accounts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {accounts.map((acc, idx) => {
            const palette = ACCOUNT_PALETTE[idx % ACCOUNT_PALETTE.length];
            const fresh = freshness(acc.last_upload_at);
            const bal = balanceFreshness(acc.as_of, acc.last_upload_at);
            const breakdown = Array.isArray(acc.breakdown) ? acc.breakdown : [];
            const hasBreakdown = breakdown.length > 0;
            return (
              <div
                key={acc.id}
                className="relative group rounded-md border border-slate-200 bg-white overflow-visible shadow-sm hover:shadow-md transition-shadow"
                data-testid={`account-card-${acc.id}`}
              >
                <div className={`h-1 bg-gradient-to-r ${palette.grad} rounded-t-md`} />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className={`h-7 w-7 grid place-items-center rounded-sm ${palette.icon}`}>
                          <Bank size={14} weight="duotone" />
                        </div>
                        <div className="text-sm font-semibold text-slate-900 truncate" title={acc.name}>{acc.name}</div>
                        {hasBreakdown && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">
                            {breakdown.length} outlets
                          </span>
                        )}
                      </div>
                    </div>
                    {fresh.stale && (
                      <Warning size={14} weight="fill" className="text-amber-500 shrink-0" data-testid={`account-stale-${acc.id}`} />
                    )}
                  </div>
                  <div className="mt-3">
                    <div className="font-mono tabular-nums text-xl font-bold text-slate-900">{fmtINR(acc.balance)}</div>
                    <div
                      className={`text-[10px] uppercase tracking-wider font-bold mt-0.5 ${
                        bal.current
                          ? "balance-asof-current"
                          : "balance-asof-stale stale-blink"
                      }`}
                      data-testid={`account-balance-asof-${acc.id}`}
                      data-balance-current={bal.current ? "true" : "false"}
                      title={bal.current
                        ? "Balance date matches upload date — current"
                        : "Balance date differs from upload date — statement may be stale"}
                    >
                      Balance as of {fmtDate(acc.as_of)}
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <Clock size={11} weight="bold" className={fresh.tone} />
                      <span className="text-slate-500">Last upload</span>
                      <span className={`font-bold ml-auto ${fresh.tone}`}>{fresh.label}</span>
                    </div>
                    {fresh.absolute !== "—" && fresh.absolute !== fresh.label && (
                      <div className="text-[10px] text-slate-400 font-mono tabular-nums text-right mt-0.5" title={acc.last_upload_at}>
                        {fresh.absolute}
                      </div>
                    )}
                  </div>
                </div>

                {hasBreakdown && (
                  <div
                    className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-20 hidden group-hover:block w-64 rounded-md border border-slate-200 bg-white shadow-lg p-3"
                    data-testid={`account-breakdown-${acc.id}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">
                      Outlet-wise breakdown
                    </div>
                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                      {breakdown.map((b, i) => (
                        <div key={`${b.outlet}-${i}`} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-slate-700 truncate" title={b.outlet}>{b.outlet}</span>
                          <span className="font-mono tabular-nums font-bold text-slate-900 shrink-0">{fmtINR(b.amount)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
                      <span className="text-slate-500 font-semibold uppercase tracking-wider text-[9px]">Total</span>
                      <span className="font-mono tabular-nums font-bold text-slate-900">{fmtINR(acc.balance)}</span>
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full -mt-px w-2 h-2 bg-white border-r border-b border-slate-200 rotate-45" />
                  </div>
                )}
              </div>
            );
          })}
          {extraCard}
        </div>
      )}
    </div>
  );
}
